import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChatMarkdown } from "@rakazo/chat-ui/web";
import type { ArtifactMedia } from "@rakazo/core";
import { Button, Dialog, DialogClose, DialogContent, DialogTitle } from "@rakazo/ui-web";
import { Code2, Download, FileText, Play, X } from "lucide-react";
import type { RefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { playableArtifactMedia } from "../lib/artifact-media-player";
import type { ArtifactTarget } from "../lib/artifact-open";
import { downloadArtifact, downloadArtifactBytes, fetchArtifactBytes } from "../lib/artifact-open";
import { useArtifactVideoPoster } from "../lib/use-artifact-video-poster";
import { useNearViewport } from "../lib/use-near-viewport";
import { errorText } from "../lib/user-error";
import { ArtifactMediaPlayer, ArtifactMediaPreview } from "./ArtifactMediaPlayer";
import { IconTooltip } from "./IconTooltip";
import { PdfViewer } from "./PdfViewer";
import { SandboxedHtmlViewer } from "./SandboxedHtmlViewer";

type ArtifactFileCardProps = {
  target: ArtifactTarget;
  artifactId: string;
  name: string;
  mimeType: string;
  size: number;
};

const PREVIEWABLE_MIME_TYPES = new Set(["text/markdown", "text/html", "application/pdf"]);
const TEXT_MIME_TYPES = new Set(["text/markdown", "text/html"]);

export function ArtifactFileCard(props: ArtifactFileCardProps) {
  const { t } = useLingui();
  const media = playableArtifactMedia(props.mimeType, props.name);
  const video = media?.kind === "video" ? media : null;
  const previewable = video !== null || PREVIEWABLE_MIME_TYPES.has(props.mimeType);
  const previewButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function startDownload() {
    setDownloadError(null);
    try {
      await downloadArtifact(props.target, props.artifactId, props.name, props.mimeType);
    } catch {
      setDownloadError(t`Could not download ${props.name}. Try again.`);
    }
  }

  if (media?.kind === "audio") {
    return (
      <div className="min-w-[280px] max-w-full rounded-2xl border border-border bg-card p-4 text-foreground">
        <AudioArtifactPreview
          key={JSON.stringify([props.target, props.artifactId, props.mimeType])}
          artifactId={props.artifactId}
          name={props.name}
          media={media}
          onDownload={startDownload}
        />
        {downloadError ? <DownloadError message={downloadError} /> : null}
      </div>
    );
  }

  if (!previewable) {
    return (
      <div>
        <button
          type="button"
          onClick={() => void startDownload()}
          className="rounded-2xl border border-border bg-card px-4 py-3 text-left text-[14px] text-foreground hover:bg-accent"
        >
          <div className="font-medium">{props.name}</div>
          <div className="mt-1 text-muted-foreground">
            {props.mimeType} · {formatBytes(props.size)}
          </div>
        </button>
        {downloadError ? <DownloadError message={downloadError} /> : null}
      </div>
    );
  }

  return (
    <>
      <div>
        {video ? (
          <VideoThumbnailCard
            {...props}
            previewButton={previewButton}
            onPreview={() => setPreviewOpen(true)}
            onDownload={() => void startDownload()}
          />
        ) : (
          <div className="flex min-w-[280px] max-w-full overflow-hidden rounded-2xl border border-border bg-card text-left text-foreground">
            <button
              ref={previewButton}
              type="button"
              aria-label={t`Preview ${props.name}`}
              onClick={() => setPreviewOpen(true)}
              className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left hover:bg-accent"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-muted text-foreground">
                {props.mimeType === "text/html" ? (
                  <Code2 size={21} strokeWidth={1.8} />
                ) : (
                  <FileText size={21} strokeWidth={1.8} />
                )}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[14px] font-medium">{props.name}</span>
                <span className="mt-0.5 block text-[13px] text-muted-foreground">
                  {formatBytes(props.size)}
                </span>
              </span>
            </button>
            <IconTooltip label={t`Download ${props.name}`}>
              <button
                type="button"
                aria-label={t`Download ${props.name}`}
                onClick={() => void startDownload()}
                className="grid w-14 shrink-0 place-items-center border-l border-border text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <Download size={19} strokeWidth={1.8} />
              </button>
            </IconTooltip>
          </div>
        )}
        {downloadError ? <DownloadError message={downloadError} /> : null}
      </div>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          showCloseButton={false}
          initialFocus={closeButton}
          finalFocus={previewButton}
          className="flex h-[min(88vh,900px)] w-[min(960px,94vw)] flex-col gap-0 overflow-hidden p-0 sm:max-w-none"
        >
          <FilePreview {...props} video={video} closeButtonRef={closeButton} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function VideoThumbnailCard({
  previewButton,
  onPreview,
  onDownload,
  ...props
}: ArtifactFileCardProps & {
  previewButton: RefObject<HTMLButtonElement | null>;
  onPreview: () => void;
  onDownload: () => void;
}) {
  const { t } = useLingui();
  const [card, near] = useNearViewport();
  const poster = useArtifactVideoPoster(props.target, props.artifactId, props.size, near);

  return (
    <div
      ref={card}
      className="w-60 max-w-full overflow-hidden rounded-[20px] border border-border bg-card text-left text-foreground"
    >
      <button
        ref={previewButton}
        type="button"
        aria-label={t`Preview ${props.name}`}
        onClick={onPreview}
        className="group relative block aspect-video w-full bg-muted"
      >
        {poster ? <img src={poster} alt="" className="size-full object-cover" /> : null}
        <span className="absolute inset-0 grid place-items-center">
          <span className="grid size-12 place-items-center rounded-full bg-foreground/60 text-background transition-colors group-hover:bg-foreground/75">
            <Play size={20} className="fill-current" />
          </span>
        </span>
      </button>
      <div className="flex border-t border-border">
        <span className="min-w-0 flex-1 px-4 py-3">
          <span className="block truncate text-[14px] font-medium">{props.name}</span>
          <span className="mt-0.5 block text-[13px] text-muted-foreground">
            {formatBytes(props.size)}
          </span>
        </span>
        <IconTooltip label={t`Download ${props.name}`}>
          <button
            type="button"
            aria-label={t`Download ${props.name}`}
            onClick={onDownload}
            className="grid w-14 shrink-0 place-items-center border-l border-border text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Download size={19} strokeWidth={1.8} />
          </button>
        </IconTooltip>
      </div>
    </div>
  );
}

function FilePreview({
  target,
  artifactId,
  name,
  mimeType,
  closeButtonRef,
  video,
}: ArtifactFileCardProps & {
  closeButtonRef: RefObject<HTMLButtonElement | null>;
  video: ArtifactMedia | null;
}) {
  const { t } = useLingui();
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ready"; bytes: Uint8Array; text?: string }
    | { status: "error"; message: string }
  >({ status: "loading" });
  const targetBotId = "botId" in target ? target.botId : undefined;
  const targetGroupId = "groupId" in target ? target.groupId : undefined;

  const streamed = video !== null;

  useEffect(() => {
    // Video streams in byte ranges from the media route; only documents are fetched whole.
    if (streamed) return;
    let cancelled = false;
    const artifactTarget: ArtifactTarget =
      targetBotId !== undefined ? { botId: targetBotId } : { groupId: targetGroupId! };
    void fetchArtifactBytes(artifactTarget, artifactId)
      .then((bytes) => {
        if (cancelled) return;
        if (!TEXT_MIME_TYPES.has(mimeType)) {
          setState({ status: "ready", bytes });
          return;
        }
        try {
          const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
          setState({ status: "ready", bytes, text });
        } catch {
          setState({ status: "error", message: t`This file is not valid UTF-8 text.` });
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: errorText(error, t`Could not load this file.`),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [artifactId, targetBotId, targetGroupId, mimeType, streamed, t]);

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-5">
        <DialogTitle className="min-w-0 flex-1 truncate text-[14px] leading-5 font-medium text-foreground">
          {name}
        </DialogTitle>
        <IconTooltip label={t`Download ${name}`}>
          <Button
            variant="ghost"
            size="icon-lg"
            className="rounded-full text-muted-foreground"
            aria-label={t`Download ${name}`}
            onClick={() =>
              void (async () => {
                setDownloadError(null);
                try {
                  if (state.status === "ready") downloadArtifactBytes(name, mimeType, state.bytes);
                  else await downloadArtifact(target, artifactId, name, mimeType);
                } catch {
                  setDownloadError(t`Could not download ${name}. Try again.`);
                }
              })()
            }
          >
            <Download />
          </Button>
        </IconTooltip>
        <DialogClose
          ref={closeButtonRef}
          aria-label={t`Close preview`}
          render={
            <Button variant="ghost" size="icon-lg" className="rounded-full text-muted-foreground" />
          }
        >
          <X />
        </DialogClose>
      </header>
      {downloadError ? (
        <div className="shrink-0 px-5 pt-4">
          <DownloadError message={downloadError} />
        </div>
      ) : null}
      <div className="relative min-h-0 flex-1 overflow-y-auto">
        {video ? (
          <ArtifactMediaPreview artifactId={artifactId} name={name} media={video} />
        ) : state.status === "loading" ? (
          <div className="p-8 text-muted-foreground">
            <Trans>Loading preview…</Trans>
          </div>
        ) : state.status === "error" ? (
          <div className="m-5 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-destructive">
            {state.message}
          </div>
        ) : mimeType === "text/html" && state.text !== undefined ? (
          <SandboxedHtmlViewer html={state.text} title={name} />
        ) : mimeType === "application/pdf" ? (
          <PdfViewer bytes={state.bytes} title={name} />
        ) : state.text !== undefined ? (
          <article className="mx-auto w-full max-w-[760px] px-8 py-10 text-[16px] leading-7 text-foreground sm:px-12 sm:py-12">
            <ChatMarkdown>{state.text}</ChatMarkdown>
          </article>
        ) : null}
      </div>
    </>
  );
}

function AudioArtifactPreview({
  artifactId,
  name,
  media,
  onDownload,
}: {
  artifactId: string;
  name: string;
  media: ArtifactMedia;
  onDownload: () => Promise<void>;
}) {
  const { t } = useLingui();
  return (
    <>
      <div className="flex items-center gap-3">
        <span className="min-w-0 flex-1 truncate text-[14px] font-medium">{name}</span>
        <button
          type="button"
          aria-label={t`Download ${name}`}
          onClick={() => void onDownload()}
          className="shrink-0 p-2 text-muted-foreground hover:text-foreground"
        >
          <Download size={19} strokeWidth={1.8} />
        </button>
      </div>
      {/* Nothing loads until the person presses play, so a chat full of clips stays cheap. */}
      <ArtifactMediaPlayer
        artifactId={artifactId}
        name={name}
        media={media}
        preload="none"
        className="mt-3 w-full"
        fallback={<DownloadError message={t`Could not load this file.`} />}
      />
    </>
  );
}

function DownloadError({ message }: { message: string }) {
  return (
    <div role="alert" className="mt-2 text-left text-[13px] text-destructive">
      {message}
    </div>
  );
}

function formatBytes(size: number) {
  const locale = i18n.locale || "en";
  if (size < 1024) return t`${size} B`;
  const format = (value: number) =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  if (size < 1024 * 1024) return t`${format(size / 1024)} KB`;
  return t`${format(size / (1024 * 1024))} MB`;
}
